import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useContext, useEffect, useState } from "react";
import { ButtonToolbar, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import type {
  PageOrientation,
  RateOfReturn,
  ReportPeriod,
  ReportSubType,
  ReportTemplateForm,
} from "../../../api/src/reports/reports.service";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import ConfirmationDialog from "../components/ConfirmationDialog";
import Content from "../components/Content";
import FormError from "../components/FormError";
import FormFieldError from "../components/FormFieldError";
import MultiSelector from "../components/MultiSelector";
import SubmitButton from "../components/SubmitButton";
import { capitalize } from "../lib/display";
import { getSchemaFieldLabel } from "../lib/forms";
import {
  useCreateReportTemplate,
  useDeleteReportTemplate,
  useQueryReportTemplate,
  useUpdateReportTemplate,
} from "./lib";

const subReportOptions: ReportSubType[] = [
  "account_summary",
  "portfolio_snapshot",
  "appraisals",
  "security_performance",
  "household_performance_attribution",
  "appraisals_wo_cost_basis",
  "performance_summary",
  "performance_chart",
  "net_investment_chart",
  "asset_allocation_top_holdings",
];

const schema: yup.ObjectSchema<ReportTemplateForm> = yup.object({
  name: yup.string().required().label("Name"),
  subReports: yup
    .array()
    .of<ReportSubType>(yup.string().required().oneOf(subReportOptions))
    .required()
    .label("Sections"),
  rateOfReturnType: yup
    .string()
    .required()
    .oneOf<RateOfReturn>(["time-weighted", "internal"])
    .default("internal")
    .label("Rate of Return"),
  orientation: yup
    .string()
    .required()
    .oneOf<PageOrientation>(["portrait", "landscape"])
    .default("portrait")
    .label("Page Orientation"),
  period: yup
    .string()
    .required()
    .oneOf<ReportPeriod>(["M", "Q", "Y"])
    .label("Period"),
});

const ReportTemplateInfo = () => {
  const { templateId } = useParams();
  const isNew = templateId === "new";

  const {
    isLoading,
    isError,
    data: reportTemplate,
  } = useQueryReportTemplate(parseInt(templateId ?? ""), { enabled: !isNew });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<ReportTemplateForm>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      orientation: "portrait",
      period: "M",
      rateOfReturnType: "internal",
      subReports: ["account_summary"],
    },
  });

  useEffect(() => {
    if (typeof reportTemplate !== "undefined") {
      reset(reportTemplate);
    }
  }, [reportTemplate, reset]);

  const createReport = useCreateReportTemplate();
  const updateReport = useUpdateReportTemplate(parseInt(templateId ?? ""));

  const notificationContext = useContext(NotificationContext);
  const navigate = useNavigate();
  const onSubmit = useCallback(
    async (data: ReportTemplateForm) => {
      try {
        if (isNew) {
          const template = await createReport.mutateAsync(data);
          notificationContext.pushNotification({
            id: `report-template-${template.id}`,
            header: "Report Template Created",
            body: `${data.name} created successfully.`,
            variant: "success",
          });
          navigate(`../${template.id}`);
        } else {
          await updateReport.mutateAsync(data);
          // reset(templateBody);
          notificationContext.pushNotification({
            id: `report-template-${templateId}`,
            header: "Report Template Updated",
            body: `${data.name} updated successfully.`,
            variant: "success",
          });
        }
      } catch {
        notificationContext.pushNotification({
          id: "report-template",
          header: isNew
            ? "Failed to Create Report Template"
            : "Failed to Update Report Template",
          body: isNew
            ? `Report template ${data.name} not created`
            : `Report template ${data.name} not updated`,
          variant: "danger",
        });
      }
    },
    [
      createReport,
      isNew,
      navigate,
      notificationContext,
      templateId,
      updateReport,
    ],
  );

  const subReports = watch("subReports");

  const deleteReportTemplate = useDeleteReportTemplate(
    parseInt(templateId ?? ""),
  );

  // "Delete" of the header opens the confirmation; only "Delete" in it sends
  // the delete (doc-report-templates-01a1888880392fec, R43 to R46). The create
  // form has no template to delete, so its "Delete" is disabled (R49, D8).
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const onDelete = useCallback(async () => {
    try {
      await deleteReportTemplate.mutateAsync();
    } catch {
      // A failed delete keeps the form open with its values (R48).
      setShowDeleteConfirm(false);
      notificationContext.pushNotification({
        id: `report-template-${templateId}`,
        header: "Failed to Delete Report Template",
        body: `Report template ${reportTemplate?.name} not deleted`,
        variant: "danger",
      });
      return;
    }
    setShowDeleteConfirm(false);
    notificationContext.pushNotification({
      id: `report-template-${templateId}`,
      header: "Report Template Deleted",
      body: `Report template ${reportTemplate?.name} deleted`,
      variant: "warning",
    });
    navigate("..");
  }, [
    deleteReportTemplate,
    navigate,
    notificationContext,
    reportTemplate?.name,
    templateId,
  ]);

  // The dialog stays open while the delete runs (R46).
  const onCloseDeleteConfirm = useCallback(() => {
    if (!deleteReportTemplate.isPending) setShowDeleteConfirm(false);
  }, [deleteReportTemplate.isPending]);

  return isLoading ? (
    <Loading message="report template" />
  ) : isError ? (
    <FormError message="An error occurred" />
  ) : typeof reportTemplate === "undefined" && !isNew ? (
    <FormError message="Report Template not found" />
  ) : (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col>
          <h1>Report Template Details</h1>
        </Col>
        <Col md="auto">
          <ButtonToolbar className="mb-3">
            <ActionButton
              label="Save"
              icon="/icons/save.svg"
              type="submit"
              className="me-3"
              disabled={isSubmitting}
            />
            <ActionButton
              variant="secondary"
              label="Delete"
              icon="/icons/trash.svg"
              type="button"
              className="me-3"
              onClick={() => {
                if (!isNew) setShowDeleteConfirm(true);
              }}
              disabled={isNew || deleteReportTemplate.isPending}
            />
            <ActionButton
              as={Link}
              to="/reports/templates"
              label="Exit"
              icon="/icons/exit.svg"
            />
          </ButtonToolbar>
        </Col>
      </Row>
      <ConfirmationDialog
        title="Delete report template?"
        message={`Report template ${reportTemplate?.name} will be deleted.`}
        showModal={showDeleteConfirm}
        isConfirming={deleteReportTemplate.isPending}
        handleConfirm={onDelete}
        handleClose={onCloseDeleteConfirm}
        confirmLabel="Delete"
      />
      <Content>
        <Row>
          <Col xl={5} lg={10}>
            <Form.Group className="mb-3" controlId="form-name">
              <Form.Label>{getSchemaFieldLabel(schema.fields.name)}</Form.Label>
              <Form.Control type="text" {...register("name")} />
              <FormFieldError field={errors.name} />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xxl={5} lg={10}>
            <Form.Group className="mb-3" controlId="form-subReports">
              <Form.Label>
                {getSchemaFieldLabel(schema.fields.subReports)}
              </Form.Label>
              <MultiSelector
                placeholder="Select included reports"
                options={subReportOptions.map((subReport) => ({
                  label: capitalize(subReport.replaceAll("_", " ")),
                  value: subReport,
                }))}
                setValue={(value: ReportSubType[]) =>
                  setValue("subReports", value)
                }
                value={subReports}
              />
              <FormFieldError field={errors.subReports} />
            </Form.Group>
          </Col>
          <Col xxl={5} lg={10}>
            <Form.Group className="mb-3" controlId="form-period">
              <Form.Label>
                {getSchemaFieldLabel(schema.fields.period)}
              </Form.Label>
              <Form.Select
                placeholder="Select report period"
                {...register("period")}
              >
                <option value="M">Last Month</option>
                <option value="Q">Last Quarter</option>
                <option value="Y">Last Year</option>
              </Form.Select>
              <FormFieldError field={errors.period} />
            </Form.Group>
          </Col>
          <Col lg={10}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="form-orientation">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.orientation)}
                  </Form.Label>
                  <Form.Select
                    placeholder="Select page orientation"
                    {...register("orientation")}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </Form.Select>
                  <FormFieldError field={errors.orientation} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="form-rateOfReturnType">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.rateOfReturnType)}
                  </Form.Label>
                  <Form.Select
                    placeholder="Select rate of return type"
                    {...register("rateOfReturnType")}
                  >
                    <option value="internal">Internal</option>
                    <option value="time-weighted">Time-Weighted</option>
                  </Form.Select>
                  <FormFieldError field={errors.rateOfReturnType} />
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col>
            <SubmitButton isSubmitting={isSubmitting} />
          </Col>
        </Row>
      </Content>
    </Form>
  );
};

export default ReportTemplateInfo;
